import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import Subscription from '../../models/Subscription';
import Job from '../../models/Job';
import dayjs from 'dayjs';
import JobWithContact from '../../models/@aggregates/JobWithContact';
import { IContact } from '@leedpulse-monorepo/interfaces';
import { JobType } from '@leedpulse-monorepo/enums';

async function getNextJobs(
  days: number,
  scopedSubscriptionId: string,
  scenarioId?: string,
  userUid?: string
): Promise<{
  jobs: JobWithContact[];
}> {
  try {
    const db = firestore;
    const jobsCollection = collection(db, FirestoreCollections.jobs);

    // We first get the corresponding subscription
    const subscriptionDoc = await getDoc(
      doc(db, FirestoreCollections.subscriptions, scopedSubscriptionId)
    );

    if (!subscriptionDoc.exists()) {
      return {
        jobs: []
      };
    }
    const subscription = Subscription.fromFirestore(subscriptionDoc);

    const today = dayjs().startOf("day");
    const period = today.add(days, "days");

    const jobConstraints = [
      where('id_api_key', '==', subscription.apiKey),
      where('timestamp', '>=', today.toDate().getTime()),
      where('timestamp', '<', period.toDate().getTime())
    ];

    // Then we get the jobs
    const results = await getDocs(
      query(
        jobsCollection,
        ...jobConstraints
      )
    );

    // The SMS planning: workflow jobs are not sent to the contact
    let jobs: JobWithContact[] = await Promise.all(
      results.docs.filter((doc) => doc.data().type !== JobType.workflow).map(async (doc) => {
        return await getJobWithContact(Job.fromFirestore(doc));
      })
    );

    if (scenarioId) {
      jobs = jobs.filter(job => job.contact.contact.prospect_category === scenarioId);
    }

    if (userUid) {
      jobs = jobs.filter(job => job.contact.contact.owner === userUid);
    }

    return {
      jobs
    };

  } catch (e) {
    console.log('error: ', e);
    throw new GetNextJobsFailure();
  }
}

async function getJobWithContact(job: Job): Promise<JobWithContact> {
  try {
    const db = firestore;
    const contactDoc = await getDoc(
      doc(db, FirestoreCollections.contacts, job.idContact)
    );
    if (!contactDoc.exists()) {
      throw new GetNextJobsFailure();
    }
    const contact = contactDoc.data() as IContact;
    return new JobWithContact(job, contact);
  } catch (e) {
    console.log('error: ', e);
    throw new GetNextJobsFailure();
  }
}

export class GetNextJobsFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getNextJobs;
